import React, { useEffect, useMemo, useRef, useState } from 'react';
import WidgetFactory from './widgets/WidgetFactory';
import { WallSectionContext } from './widgets/wallSection';
import { dashboardsAPI } from '../services/api';
import { breakpointFor, gridColumns, layoutItemsFor, rowHeightFor, sectionsToRepeat, widescreenSlots } from './dashboard-editor/editorUtils';
import { useContinuousScroll } from '../hooks/useAutoScroll';

const GAP_PX = 10;
const HEADING_PX = 70;
// Picks up changes made to the dashboards while a screen is left running.
const RELOAD_MS = 15 * 60 * 1000;

/** One dashboard's widgets at the size they have on its own page. */
const WallSection = React.forwardRef(({ dashboard, breakpoint, rowHeight, repeat = false }, ref) => {
  const section = useMemo(() => ({
    dateRange: dashboard.default_date_range || null,
    staleThresholdMinutes: dashboard.stale_threshold_minutes,
  }), [dashboard.default_date_range, dashboard.stale_threshold_minutes]);
  const widgets = dashboard.widgets || [];
  const wide = widescreenSlots(widgets);

  return (
    <WallSectionContext.Provider value={section}>
      <section className="wall-section" ref={ref} aria-label={repeat ? undefined : dashboard.name} aria-hidden={repeat || undefined} data-testid={repeat ? 'wall-section-repeat' : 'wall-section'}>
        <h2 className="wall-section-name">{dashboard.name}</h2>
        <div className="wall-section-grid" style={{ gridTemplateColumns: `repeat(${gridColumns(breakpoint)}, minmax(0, 1fr))`, gridAutoRows: `${rowHeight}px`, gap: `${GAP_PX}px` }}>
          {widgets.map((widget) => {
            const { x, y, w, h } = layoutItemsFor(widget, wide.get(widget.id))[breakpoint];
            return (
              <div key={widget.id} className="wall-section-cell" style={{ gridColumn: `${x + 1} / span ${w}`, gridRow: `${y + 1} / span ${h}` }}>
                <WidgetFactory widget={widget} />
              </div>
            );
          })}
        </div>
      </section>
    </WallSectionContext.Provider>
  );
});
WallSection.displayName = 'WallSection';

/**
 * The chosen dashboards as one long page that scrolls without end. The start of
 * the list is repeated after the end, so the page can jump back to the real
 * start while showing exactly the same thing.
 */
const ContinuousWall = ({ dashboardIds }) => {
  const [dashboards, setDashboards] = useState(null);
  const [width, setWidth] = useState(0);
  const [rootEl, setRootEl] = useState(null);
  const firstRef = useRef(null);
  const repeatRef = useRef(null);
  const idsKey = dashboardIds.join(',');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const results = await Promise.all(dashboardIds.map((id) => dashboardsAPI.getById(id).catch(() => null)));
      if (!cancelled) setDashboards(results.filter((r) => r?.success).map((r) => r.data));
    };
    load();
    const timer = setInterval(load, RELOAD_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey]);

  useEffect(() => {
    if (!rootEl) return undefined;
    const update = () => setWidth(rootEl.clientWidth);
    update();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(update);
    observer.observe(rootEl);
    return () => observer.disconnect();
  }, [rootEl]);

  const breakpoint = breakpointFor(width);
  const rowHeight = rowHeightFor(window.innerWidth);
  const repeats = useMemo(() => {
    const heights = (dashboards || []).map((d) => {
      const wide = widescreenSlots(d.widgets || []);
      const rows = (d.widgets || []).reduce((most, w) => {
        const { y, h } = layoutItemsFor(w, wide.get(w.id))[breakpoint];
        return Math.max(most, y + h);
      }, 0);
      return HEADING_PX + rows * (rowHeight + GAP_PX);
    });
    return sectionsToRepeat(heights, window.innerHeight);
  }, [dashboards, breakpoint, rowHeight]);

  const ready = Boolean(dashboards?.length && width);
  useContinuousScroll(ready, idsKey, firstRef, repeatRef);

  return (
    <div className="wall-continuous" ref={setRootEl} data-testid="wall-continuous">
      {dashboards === null && <p className="wall-continuous-note">Loading…</p>}
      {dashboards?.length === 0 && <p className="wall-continuous-note">None of the chosen dashboards could be loaded.</p>}
      {ready && dashboards.map((dashboard, n) => (
        <WallSection key={dashboard.id} ref={n === 0 ? firstRef : undefined} dashboard={dashboard} breakpoint={breakpoint} rowHeight={rowHeight} />
      ))}
      {ready && repeats.map((index, n) => (
        <WallSection key={`repeat-${n}`} ref={n === 0 ? repeatRef : undefined} dashboard={dashboards[index]} breakpoint={breakpoint} rowHeight={rowHeight} repeat />
      ))}
    </div>
  );
};

export default ContinuousWall;
